import { Rss } from "lucide-react";
import GithubSlugger from "github-slugger";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { StartFreeLink } from "@/components/cta-buttons";
import { useTranslation } from "@/common/providers/i18n-provider";
import { CHANGELOG_FEED } from "@/config/tag-feeds";
import { formatDateLocalized, formatPostCount } from "@/lib/format";
import { groupPostsByYear, type ChangelogEntry } from "@/lib/changelog";
import { localeUrlFor } from "@/lib/seo";
import "@/styles/company-pages.css";
import "@/styles/reference-landings.css";

/**
 * The `/changelog` reading surface, over the `changelog`-tagged blog stream.
 *
 * ## Why this is not a wrapper around `BlogTagPage`
 *
 * `BlogTagPage` renders a *taxonomy archive*: it leads with `#changelog` and a
 * "browse all tags" link, then lays the posts out as an equal-weight card grid
 * paginated at 12. That framing answers "what else is filed under this tag?".
 *
 * A changelog answers a different question — "what has shipped since I last
 * looked?" — so chronology is the primary axis, not a per-card byline. Hence
 * the year rail, the whole stream on one page, the newest entry flagged, and
 * a feed link in the header
 * instead of a link back into the tag vocabulary.
 *
 * Everything genuinely shared is still shared: the data comes from the same
 * `loadTagPosts` shard through the existing `blogTag` GraphQL field, and the
 * chrome, links, date and plural helpers are the blog's own.
 */
export function ChangelogPage({ posts }: { posts: readonly ChangelogEntry[] }) {
  const { t, locale } = useTranslation();
  const slugger = new GithubSlugger();
  const groups = groupPostsByYear(posts).map((group) => ({
    ...group,
    anchor: slugger.slug(`release-year-${group.year}`),
  }));
  const latest = posts[0];
  // The feeds are Nitro handlers, not router routes, so this is a plain anchor
  // rather than a `LocaleLink`. `localeUrlFor` with an empty origin applies the
  // same locale-prefix rule the router does, against the registry's own path.
  const feedHref = localeUrlFor(
    "",
    locale,
    `${CHANGELOG_FEED.basePath}/rss.xml`,
  );

  return (
    <div className="company-page reference-landing release-page">
      <AppHeader />

      <main id="main">
        <header className="company-container reference-hero">
          <div>
            <p className="reference-context">{t("changelog.hero.eyebrow")}</p>
            <h1>{t("changelog.hero.title")}</h1>
          </div>
          <div>
            <p className="company-lead">{t("changelog.hero.subtitle")}</p>
            <div className="release-meta">
              <span>{formatPostCount(t, posts.length)}</span>
              <a href={feedHref} className="company-text-link release-feed">
                <Rss size={18} aria-hidden="true" />
                {t("changelog.subscribe")}
              </a>
            </div>
          </div>
        </header>

        {groups.length > 1 ? (
          <nav
            className="company-container reference-index"
            aria-label={t("theme.TOCCollapsible.toggleButtonLabel")}
          >
            <p>{t("theme.TOCCollapsible.toggleButtonLabel")}</p>
            <ul>
              {groups.map(({ year, anchor }) => (
                <li key={anchor}>
                  <a href={`#${anchor}`}>{year}</a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {groups.length === 0 ? (
          <div className="company-container release-empty">
            <p>{t("changelog.empty")}</p>
          </div>
        ) : (
          <div className="company-container release-history">
            {groups.map(({ year, posts: yearPosts, anchor }) => (
              <section
                key={anchor}
                id={anchor}
                aria-labelledby={`heading-${anchor}`}
                className="release-year"
              >
                {/* The year is the rail: it sticks alongside its own entries so
                    the reader always knows which release cycle they're in. */}
                <h2 id={`heading-${anchor}`}>{year}</h2>

                <ol>
                  {yearPosts.map((post) => (
                    <li
                      key={`${post.date}-${post.slug}`}
                      className={
                        post === latest
                          ? "release-entry release-latest"
                          : "release-entry"
                      }
                    >
                      <LocaleLink
                        to="/blog/$year/$month/$day/$slug"
                        params={{
                          year: post.year,
                          month: post.month,
                          day: post.day,
                          slug: post.slug,
                        }}
                        preload="viewport"
                        className="release-link"
                      >
                        <div className="release-date">
                          <time dateTime={post.date}>
                            {formatDateLocalized(post.date, locale) ??
                              post.date}
                          </time>
                          {post === latest ? (
                            <span className="release-latest-label">
                              {t("changelog.latest")}
                            </span>
                          ) : null}
                        </div>
                        <h3>{post.title}</h3>
                        {post.description ? <p>{post.description}</p> : null}
                      </LocaleLink>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        )}
        <div className="company-container reference-actions">
          <StartFreeLink surface="changelog" />
          <LocaleLink to="/docs" className="company-text-link">
            {t("homepage.manifestoDocsTitle")}
          </LocaleLink>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
